import React, { useEffect, useRef, useState } from "react";
import { useAppMakePickModalApiResponseSelector } from "src/_common/hooks/selectors/eventSelector";
import { useEventApi } from "src/_common/hooks/actions/event/appEventApiHook";
import { Modal } from "react-bootstrap";

interface RodeoEvent {
  id: string;
  name: string;
  status: "picked" | "unpicked";
  selectedRider?: {
    name: string;
    avatar: string;
  };
}

interface MakePicksModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  onPicksChange?: () => void; // Callback when picks are updated
  eventData?: {
    rodeo_event_id?: string;
    performance_id?: string;
    group_id?: string;
    title: string;
    location: string;
    date: string;
    events?: RodeoEvent[];
  };
}

const defaultEventData = {
  rodeo_event_id: "",
  performance_id: "",
  group_id: "",
  title: "Summer Rodeo Run",
  location: "Pasco, WA",
  date: "August 13 - 8:45PM CST",
  events: [
    {
      id: "bareback-riding",
      name: "Bareback Riding",
      status: "picked" as const,
      selectedRider: {
        name: "Jake Thompson",
        avatar: "/img/avatar/avatar-1.png",
      },
    },
    {
      id: "saddle-bronc",
      name: "Saddle Bronc",
      status: "picked" as const,
      selectedRider: {
        name: "Hero Runner",
        avatar: "/img/avatar/avatar-2.png",
      },
    },
    {
      id: "bull-riding",
      name: "Bull Riding",
      status: "picked" as const,
      selectedRider: {
        name: "Ryan Foster",
        avatar: "/img/avatar/avatar-3.png",
      },
    },
    {
      id: "steer-wrestling",
      name: "Steer Wrestling",
      status: "picked" as const,
      selectedRider: {
        name: "Tanner Wells",
        avatar: "/img/avatar/avatar-1.png",
      },
    },
    {
      id: "team-roping",
      name: "Team Roping",
      status: "unpicked" as const,
    },
    {
      id: "tie-down-roping",
      name: "Tie-Down Roping",
      status: "unpicked" as const,
    },
    {
      id: "barrel-racing",
      name: "Barrel Racing",
      status: "unpicked" as const,
    },
    {
      id: "breakaway-roping",
      name: "Breakaway Roping",
      status: "unpicked" as const,
    },
  ],
};

const MakePicksModal: React.FC<MakePicksModalProps> = ({
  isOpen,
  onClose,
  className = "",
  onPicksChange,
  eventData = defaultEventData,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [showLegend, setShowLegend] = useState(false);
  const eventApi = useEventApi();
  const eventContestantList = useAppMakePickModalApiResponseSelector();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickedRiders, setPickedRiders] = useState<{ [eventId: string]: string }>({});
  // Fetch events from API when modal opens
  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = {
      rodeo_event_id: eventData?.rodeo_event_id,
      performance_id: eventData?.performance_id,
      group_id: eventData?.group_id,
    };
    eventApi.callGetRodeoEventEvents(
      params,
      (_msg: string, _resp: any) => {
        setLoading(false);
        setError(null);
      },
      (_err: string) => {
        setLoading(false);
        setError("Failed to fetch rodeo event events");
      }
    );
  }, []);

  useEffect(() => {
    // Set first tab as active when data is loaded
    if (eventContestantList?.event_dtls && eventContestantList.event_dtls.length > 0) {
      setActiveTab(eventContestantList.event_dtls[0].id);
      getFilteredEventsContestant();
    }
  }, [eventContestantList]);

  const handlePickClick = (riderId: string) => {
    // Check if winning_declare is 0 (picks are still allowed)
    const currentEvent = getFilteredEventsContestant();
    if (currentEvent?.winning_declare !== 0) {
      console.log("Cannot pick: Winners have already been declared for this event");
      return;
    }

    const params = {
      rider_id: riderId,
      event_id: activeTab,
      performance_id: eventData?.performance_id,
      rodeo_event_id: eventData?.rodeo_event_id,
    };

    eventApi.callRodeoEventUserPick(
      params,
      (msg: string, resp: any) => {
        console.log("Pick successful:", resp);
        // Update local state to mark this rider as picked for this event
        setPickedRiders((prev) => ({
          ...prev,
          [activeTab!]: riderId,
        }));
        // Call the callback to update pick counts in parent component
        onPicksChange?.();
      },
      (err: string) => {
        console.error("Pick failed:", err);
        // TODO: Show error message to user
      }
    );
  };

  const handleTabClick = (tabType: string) => {
    setActiveTab(tabType);
    getFilteredEventsContestant();
  };

  // Use API events if available, otherwise fallback to static
  // const eventsList = (apiEvents && apiEvents.length > 0 ? apiEvents : eventData?.events) || [];
  // // eventsList now uses apiEvents from selector

  const getFilteredEventsContestant = () => {
    if (!eventContestantList?.event_dtls || !activeTab) return [];

    const filteredEvent = eventContestantList.event_dtls.filter((event: any) => event.id === activeTab)?.[0];

    return filteredEvent || [];
  };

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      className={`make-picks-modal-new ${className}`}
      centered
      size="lg"
    >
      <Modal.Body className="p-0">
        <div
          ref={modalRef}
          className="make-picks-modal-new-content"
          role="dialog"
          aria-modal="true"
          aria-label="Make Picks Modal"
          tabIndex={-1}
        >
          {/* Close button */}
          <button className="btn-close-modal" onClick={onClose} aria-label="Close modal">
            ×
          </button>

          {/* Header */}
          <div className="modal-header-section">
            <div className="event-logo">
              <img
                src={eventContestantList?.performance_dtls?.avatar.thumb || "/img/avatar/avatar-1.png"}
                alt="Avatar"
              />
            </div>
            <div className="event-details">
              <h2 className="event-title">{eventContestantList?.group_dtls?.title}</h2>
              <strong className="text_yellow">{eventContestantList?.rodeo_event_dtls?.title}</strong>
              <p className="event-location">
                {eventContestantList?.rodeo_event_dtls?.location} {eventContestantList?.performance_dtls?.title}
              </p>
            </div>
          </div>

          {/* Status tags */}
          <div className="status-tags-section">
            {eventContestantList?.event_dtls.map((events: any, index: number) => {
              const isEventPicked = events.ispicked || pickedRiders[events.id];
              return (
                <span
                  key={index}
                  className={`status-tag ${!isEventPicked ? "no-picks" : "picks-made"} ${
                    activeTab === events.id ? "active" : ""
                  }`}
                  onClick={() => handleTabClick(events.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleTabClick(events.id);
                    }
                  }}
                >
                  {isEventPicked ? (
                    <i className="fas fa-check"></i>
                  ) : activeTab === events.id ? (
                    <i className="eos-icons_loading"></i>
                  ) : null}

                  {events.event_name}
                </span>
              );
            })}
          </div>

          {/* Show Legend Toggle */}
          <div className="legend-section">
            <button
              className="show-legend-btn"
              onClick={() => setShowLegend(!showLegend)}
              aria-expanded={showLegend}
              aria-controls="legend-content"
            >
              <span>Show Legend</span>
              <i className={`fas fa-chevron-${showLegend ? "up" : "down"}`}></i>
            </button>

            {/* Legend Content */}
            {showLegend && (
              <div id="legend-content" className="legend-content">
                <div className="legend-item">
                  <span className="legend-badge no-picks">
                    <i className="fas fa-circle"></i>
                    Team Roping
                  </span>
                  <span className="legend-label">No Picks Yet</span>
                </div>

                <div className="legend-item">
                  <span className="legend-badge currently-picking">
                    <i className="eos-icons_loading"></i>
                    Barrel Racing
                  </span>
                  <span className="legend-label">Currently Picking</span>
                </div>

                <div className="legend-item">
                  <span className="legend-badge picks-made">
                    <i className="fas fa-check"></i>
                    Team Roping
                  </span>
                  <span className="legend-label">Picks made</span>
                </div>

                <div className="legend-item">
                  <span className="legend-badge picks-closed">
                    <i className="fas fa-times"></i>
                    Steer Wrestling
                  </span>
                  <span className="legend-label">Not picked - winner not declared + pick closed</span>
                </div>

                <div className="legend-item">
                  <span className="legend-badge picks-closed">
                    <i className="fas fa-times"></i>
                    Steer Wrestling
                  </span>
                  <span className="legend-label">Not picked - winner not declared + pick closed</span>
                </div>
              </div>
            )}
          </div>

          {/* Riders list */}
          <div className="events-list-section">
            {loading && <div className="loader">Loading events...</div>}
            {/* {error && <div className="error-message">{error}</div>} */}
            {!loading && !error && getFilteredEventsContestant()?.riders?.length === 0 && (
              <div className="no-events">No events available.</div>
            )}
            {!loading &&
              !error &&
              getFilteredEventsContestant()?.riders?.map((rider: any, index: number) => (
                <div key={index} className={`event-item ${rider.status}`}>
                  <div className="event-info">
                    <div className="event-details-main">
                      {rider.rider && (
                        <div className="selected-rider">
                          <img src={rider.rider.thumb} alt={rider.name} className="rider-avatar" />
                          <span className="rider-name">{rider.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="event-actions">
                    {pickedRiders[activeTab!] === rider.id ||
                    (pickedRiders[activeTab!] === undefined &&
                      getFilteredEventsContestant()?.ispicked?.rider_id === rider.id) ? (
                      <span className="picked-status">Picked</span>
                    ) : getFilteredEventsContestant()?.winning_declare !== 0 ? (
                      <button className="btn-pick disabled" disabled>
                        Closed
                      </button>
                    ) : (
                      <button className="btn-pick" onClick={() => handlePickClick(rider.id)}>
                        Pick
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MakePicksModal;
